import React from 'preact/compat';
import propTypes from 'prop-types';
import analyticsService from 'general/js/analytics-service';
import { formatNumber } from 'general/js/utils';

import CustomAcitivityHelper from 'components/custom-activity-logger/js/custom-activity-helper';

class StampDutyCalculator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      price: this.props.propertyValue,
      isFirstTimeBuyer: false,
      isSecondHome: false,
      isNonResident: false,
      hasError: false,
      showResult: false,
      calculatedStampDuty: 0,
      effectiveRate: 0,
      buyerType: 'nextHome',
      calculatedBands: [],
      useNonResidentBands: false,
      isPropertyPageVersion: this.props.widgetType === 'PROPERTY'
    };

    this.fields = {
      price: 'price',
      firstTimeBuyer: 'isFirstTimeBuyer',
      secondHome: 'isSecondHome',
      nonResident: 'isNonResident'
    }

  }

  _handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const isBuyerStatusField = name === this.fields.firstTimeBuyer || name === this.fields.secondHome;
    const buyerStatusExists = this.state.isFirstTimeBuyer || this.state.isSecondHome;

    if (isBuyerStatusField && buyerStatusExists) {

      const isFirstTimeBuyer = name === this.fields.firstTimeBuyer && target.checked;
      const isSecondHome = name === this.fields.secondHome && target.checked;

      this.setState({
        isFirstTimeBuyer,
        isSecondHome,
      });

    } else {

      if (target.type === 'checkbox') {
        this.setState({ [name]: value });
      } else {
        if (value.startsWith('0')) {
          this.setState({ [name]: '' });
          return '';
        }

        const numericValue = value.replace(/[^0-9]/g, '');
        if (numericValue.startsWith('-')) {
          this.setState({ [name]: '' });
        }

        this.setState({
          [name]: numericValue.substring(0, 10),
        });
      }

    }
  }

  _handleValueBlur = (event) => {
    const isValid = event.target.validity.valid;

    this.setState({
      hasError: !isValid,
    });
  }

  _handleFormSubmit = (event) => {
    const { hasError, isNonResident } = this.state;

    event.preventDefault();

    if (!hasError) {
      const { 
        calculatedBands, 
        calculatedStampDuty, 
        effectiveRate 
      } = this._calculateValues();

      const useNonResidentBands = isNonResident;

      this.setState({
        showResult: true,
        calculatedBands,
        calculatedStampDuty,
        effectiveRate,
        useNonResidentBands
      });

      this._sendAnalytics();
      this._handleCustomActivityTracking();
    }
  }

  _calculateValues = () => {
    const { bands, firstTimeBuyerLimit } = this.props;
    const { price, isNonResident } = this.state;

    const priceIsBelowFirstTimeBuyerLimit = !firstTimeBuyerLimit || price < firstTimeBuyerLimit;

    const buyerType = this.state.isFirstTimeBuyer && priceIsBelowFirstTimeBuyerLimit ? "firstTimeBuyer" :
                      this.state.isSecondHome ? "secondHome" :
                      "nextHome";
    const buyerTypeBands = bands[buyerType];

    const taxType = isNonResident ? "nonUKPercentage" : "percentage";

    let calculatedStampDuty = 0;
    let calculatedBands = [];
    let total = 0;

    buyerTypeBands.forEach(band => {
      const { min, max } = band;

      if (price < min) {
        calculatedBands.push({
          ...band, 
          bandValue: 0,
          bandTax: 0
        });

        return;
      }

      const bandTopRange = max ? 
                              (price > max) ? max : price 
                              : price;
      const bandValue = bandTopRange - total;
      const bandTax = bandValue / 100 * band[taxType];
      calculatedStampDuty += bandTax;
      total += bandValue;

      calculatedBands.push({
        ...band,
        bandValue,
        bandTax
      });
    });

    const effectiveRate = (calculatedStampDuty / price) * 100;

    return {
      calculatedBands,
      calculatedStampDuty,
      effectiveRate
    }
  }

  _formatPriceRange = (min, max) => {
    const formattedMin = `£${min.toLocaleString()}`;
    
    if (!max) return `${formattedMin} +`;

    const formattedMax = `£${max.toLocaleString()}`;
    return `${formattedMin} - ${formattedMax}`;
    
  }

  _formatPrice = (price) => {
    let formattedPrice = price.toLocaleString();

    if (formattedPrice.includes('.') && 
        formattedPrice.indexOf('.') === formattedPrice.length - 2) {
          formattedPrice = formattedPrice + '0';
        }
    return `£${formattedPrice}`;
  }

  _sendAnalytics = () => {
    const CATEGORY = "Stamp Duty Calculator";
    const { price, isFirstTimeBuyer, isSecondHome, isNonResident } = this.state;
    const { widgetType: pageType } = this.props;
    const buyerType = isFirstTimeBuyer ? "first time" :
                      isSecondHome ? "second property" :
                      "next property";

    analyticsService.sendEvent(CATEGORY, `Value entered: ${price}`, pageType);
    analyticsService.sendEvent(CATEGORY, `Buying category: ${buyerType}`, pageType);
    if (isNonResident) analyticsService.sendEvent(CATEGORY, `Non UK Resident`, pageType);
  }

  _handleCustomActivityTracking() {
    const pageActivityLogger = document.querySelector('[data-dc-custom-activity-logger][data-page-level]');

    if (pageActivityLogger) {
      const { detailId, itemId, valueString } = pageActivityLogger.dataset;
      const { price, isFirstTimeBuyer, isSecondHome, isNonResident } = this.state;

      const items = {
        PropertyPrice: price,
        FirstTimeBuyer: isFirstTimeBuyer,
        SecondHome: isSecondHome,
        NonUKResident: isNonResident
      }

      const newValueString = CustomAcitivityHelper.addItemsToValueString(valueString, items);

      const params = {
        activityTitle: 'Stamp Duty Calculator',
        activityType: 'StampDutyCalculator',
        detailId,
        itemId,
        valueString: newValueString
      }

      CustomAcitivityHelper.send(params);
    }
  }

  render() {
    const { 
      priceLabel,
      firstTimeBuyerLabel, 
      secondHomeLabel, 
      nonResidentLabel,
      buttonLabel,
      valueError,
      toPayLabel,
      effectiveRateLabel,
      taxBandLabel,
      percentageLabel,
      taxableSumLabel,
      taxLabel
    } = this.props;

    const {
      price,
      isFirstTimeBuyer,
      isSecondHome,
      isNonResident,
      hasError,
      showResult,
      calculatedStampDuty,
      effectiveRate,
      calculatedBands,
      useNonResidentBands,
      isPropertyPageVersion
    } = this.state;

    return (
      <>
        <form 
          className="stamp-duty-calculator__form form"
          onSubmit={this._handleFormSubmit}
        >
          <div className="form__row">
            <div className="form-control stamp-duty-calculator__price-field">
              <label 
                className="form-control__label form-control__label--bold"
                htmlFor="stc-property-price"
              >
                {priceLabel}
              </label>
              <div className="form-control__content">
                <div 
                  className="input-prefix" 
                  data-prefix="&pound;"
                >
                  <input
                    id="stc-property-price"
                    name={this.fields.price}
                    className="input-text input-prefix__input input-text--border input-text--round"
                    type="text" 
                    pattern="([0-9]+.{0,1}[0-9]*,{0,1})*[0-9]"
                    required
                    value={formatNumber(price)}
                    onChange={this._handleInputChange}
                    onBlur={this._handleValueBlur}
                    aria-describedby={hasError ? 'stc-price-error' : false}
                  />
                </div>
                {hasError && (
                    <ul 
                      className="form-control-errors filled"
                      aria-live="assertive"
                    >
                      <li className="parsley-required">
                        {valueError}
                      </li>
                    </ul>
                  )}
              </div>
            </div>
          </div>
          <div className="form__row">
            <div className={`form-control checkbox ${!isPropertyPageVersion && `checkbox--light`}`}>
              <input 
                type="checkbox"
                id="stc-first-time-buyer"
                name={this.fields.firstTimeBuyer}
                checked={isFirstTimeBuyer}
                onChange={this._handleInputChange}
              />
              <label 
                className="checkbox__label form-control__label form-control__label--bold"
                htmlFor="stc-first-time-buyer"
              >
                {firstTimeBuyerLabel}
              </label>
            </div>
          </div>
          <div className="form__row">
            <div className={`form-control checkbox ${!isPropertyPageVersion && `checkbox--light`}`}>
              <input 
                type="checkbox"
                id="stc-second-home"
                name={this.fields.secondHome}
                checked={isSecondHome}
                onChange={this._handleInputChange}
              />
              <label 
                className="checkbox__label form-control__label form-control__label--bold"
                htmlFor="stc-second-home"
              >
                {secondHomeLabel}
              </label>
            </div>
          </div>
          <div className="form__row">
            <div className={`form-control checkbox ${!isPropertyPageVersion && `checkbox--light`}`}>
              <input 
                type="checkbox"
                id="stc-non-resident"
                name={this.fields.nonResident}
                checked={isNonResident}
                onChange={this._handleInputChange}
              />
              <label 
                className="checkbox__label form-control__label form-control__label--bold"
                htmlFor="stc-non-resident"
              >
                {nonResidentLabel}
              </label>
            </div>
          </div>
          <div className="form__footer stamp-duty-calculator__form-footer">
            <button className="btn" aria-controls="stc-result">
              {buttonLabel}
            </button>
          </div>
        </form>
        <section 
          id="stc-result" 
          className={`stamp-duty-calculator__result ${showResult ? '' : 'stamp-duty-calculator__result--not-expanded'}`}
          role="region"
          aria-live="polite"
          aria-label="Stamp duty breakdown"
        >
          { showResult && (
            <div className="stamp-duty-calculator__result-inner">
              <div className="stamp-duty-calculator__rate-block">
                <h3 class="stamp-duty-calculator__rate-title">{toPayLabel}</h3>
                <p class="stamp-duty-calculator__value">{this._formatPrice(calculatedStampDuty)}</p>
                <p class="stamp-duty-calculator__effective-rate">{effectiveRateLabel} {effectiveRate.toFixed(1)}%</p>
              </div>
              
              <table className="stamp-duty-calculator__rate-breakdown">
                <thead>
                  <tr>
                    <th scope="col">{taxBandLabel}</th>
                    <th scope="col">{percentageLabel}</th>
                    <th scope="col">{taxableSumLabel}</th>
                    <th scope="col">{taxLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  { 
                    calculatedBands.map((band) => (
                      <tr key={band.id}>
                        <td>{this._formatPriceRange(band.min, band.max)}</td>
                        <td>{useNonResidentBands ? band.nonUKPercentage : band.percentage}%</td>
                        <td>{this._formatPrice(band.bandValue)}</td>
                        <td>{this._formatPrice(band.bandTax)}</td>
                      </tr>
                    )) 
                  }
                </tbody>
              </table>

            </div>
          )}
        </section>
      </>
    )
  }
};

StampDutyCalculator.propTypes = {
  priceLabel: propTypes.string,
  firstTimeBuyerLabel: propTypes.string,
  secondHomeLabel: propTypes.string,
  nonResidentLabel: propTypes.string,
  buttonLabel: propTypes.string,
  propertyValue: propTypes.number,
  valueError: propTypes.string,
  toPayLabel: propTypes.string,
  effectiveRateLabel: propTypes.string,
  taxBandLabel: propTypes.string,
  percentageLabel: propTypes.string,
  taxableSumLabel: propTypes.string,
  taxLabel: propTypes.string,
  bands: propTypes.array,
  firstTimeBuyerLimit: propTypes.number,
  widgetType: propTypes.string
}

export default StampDutyCalculator;